import {
  temperatureBasisSchema,
  temperatureRiseMethodSchema,
  verificationOutcomeSchema,
} from "../services/busbarSelectionContract";
import {
  TEMPERATURE_BASIS_LABELS,
  TEMPERATURE_RISE_METHOD_LABELS,
  VERIFICATION_OUTCOME_LABELS,
  type BusbarSelectionDraft,
} from "./busbarSelectionDraft";
import {
  anyFilled,
  blockMemo,
  fieldKit,
  OptionalBlock,
  type BlockProps,
} from "./BusbarSelectionFields";

// The optional blocks of the busbar selection study that are plain fields: each one is a
// collapsed details element, a memoized component of its own slice of the draft, and holds
// no engineering rule (the backend engine is the single source of truth).

const ARRANGEMENT_KEYS = [
  "arrangementBars",
  "arrangementWidthMm",
  "arrangementThicknessMm",
] as const;

type ArrangementValues = Pick<BusbarSelectionDraft, (typeof ARRANGEMENT_KEYS)[number]>;

export const SelectedArrangementBlock = blockMemo(function SelectedArrangementBlock({
  draft,
  onChange,
  disabled,
}: BlockProps<ArrangementValues>) {
  const kit = fieldKit(draft, onChange);
  return (
    <OptionalBlock
      title="Selected arrangement"
      filled={anyFilled(draft.arrangementBars, draft.arrangementWidthMm, draft.arrangementThicknessMm)}
      disabled={disabled}
    >
      {kit.decimal("Bars per phase", "arrangementBars")}
      {kit.decimal("Bar width (mm)", "arrangementWidthMm")}
      {kit.decimal("Bar thickness (mm)", "arrangementThicknessMm")}
    </OptionalBlock>
  );
}, ARRANGEMENT_KEYS);

const SHORT_CIRCUIT_KEYS = [
  "shortCircuitIcwKa",
  "shortCircuitIcwDurationS",
  "shortCircuitIpkKa",
  "shortCircuitDocument",
  "shortCircuitRevisionOrDate",
  "shortCircuitPageOrClause",
] as const;

type ShortCircuitValues = Pick<BusbarSelectionDraft, (typeof SHORT_CIRCUIT_KEYS)[number]>;

export const ShortCircuitBlock = blockMemo(function ShortCircuitBlock({
  draft,
  onChange,
  disabled,
}: BlockProps<ShortCircuitValues>) {
  const kit = fieldKit(draft, onChange);
  return (
    <OptionalBlock
      title="Short-circuit ratings"
      filled={anyFilled(
        draft.shortCircuitIcwKa,
        draft.shortCircuitIcwDurationS,
        draft.shortCircuitIpkKa,
      )}
      disabled={disabled}
    >
      {kit.decimal("Rated short-time withstand current Icw (kA)", "shortCircuitIcwKa")}
      {kit.decimal("Icw duration (s)", "shortCircuitIcwDurationS")}
      {kit.decimal("Rated peak withstand current Ipk (kA)", "shortCircuitIpkKa")}
      {kit.reference(["shortCircuitDocument", "shortCircuitRevisionOrDate", "shortCircuitPageOrClause"])}
    </OptionalBlock>
  );
}, SHORT_CIRCUIT_KEYS);

const TEMPERATURE_RISE_KEYS = [
  "temperatureRiseMethod",
  "temperatureRiseReferenceDesign",
  "temperatureRiseVerifiedRatedCurrentA",
  "temperatureRiseAmbientBasisC",
  "temperatureBasis",
  "temperatureValue",
  "temperaturePermissibleCriterionValue",
  "temperatureVerificationDocument",
  "temperatureDocument",
  "temperatureRevisionOrDate",
  "temperaturePageOrClause",
] as const;

type TemperatureRiseValues = Pick<BusbarSelectionDraft, (typeof TEMPERATURE_RISE_KEYS)[number]>;

export const TemperatureRiseBlock = blockMemo(function TemperatureRiseBlock({
  draft,
  onChange,
  disabled,
}: BlockProps<TemperatureRiseValues>) {
  const kit = fieldKit(draft, onChange);
  return (
    <OptionalBlock
      title="Temperature rise evidence"
      filled={anyFilled(...TEMPERATURE_RISE_KEYS.map((key) => draft[key]))}
      disabled={disabled}
    >
      {kit.choice(
        "Verification method",
        "temperatureRiseMethod",
        temperatureRiseMethodSchema.options,
        TEMPERATURE_RISE_METHOD_LABELS,
      )}
      {kit.text("Reference design", "temperatureRiseReferenceDesign")}
      {kit.decimal("Verified rated current (A)", "temperatureRiseVerifiedRatedCurrentA")}
      {kit.decimal("Ambient of the verification (°C)", "temperatureRiseAmbientBasisC")}
      {kit.choice(
        "Temperature figure is a",
        "temperatureBasis",
        temperatureBasisSchema.options,
        TEMPERATURE_BASIS_LABELS,
      )}
      {kit.decimal("Temperature figure (K or °C)", "temperatureValue")}
      {kit.decimal("Permissible criterion (K or °C)", "temperaturePermissibleCriterionValue")}
      {kit.text("Verification document", "temperatureVerificationDocument")}
      {kit.reference(["temperatureDocument", "temperatureRevisionOrDate", "temperaturePageOrClause"])}
    </OptionalBlock>
  );
}, TEMPERATURE_RISE_KEYS);

const SUPPORTS_KEYS = [
  "supportSpacingMm",
  "phaseSpacingMm",
  "supportType",
  "supportManufacturerReference",
  "supportDeclaredMaxSpacingMm",
  "supportDeclaredPeakKa",
  "supportDocument",
  "supportRevisionOrDate",
  "supportPageOrClause",
] as const;

type SupportsValues = Pick<BusbarSelectionDraft, (typeof SUPPORTS_KEYS)[number]>;

export const SupportsBlock = blockMemo(function SupportsBlock({
  draft,
  onChange,
  disabled,
}: BlockProps<SupportsValues>) {
  const kit = fieldKit(draft, onChange);
  return (
    <OptionalBlock
      title="Supports"
      filled={anyFilled(...SUPPORTS_KEYS.map((key) => draft[key]))}
      disabled={disabled}
    >
      {kit.decimal("Support spacing (mm)", "supportSpacingMm")}
      {kit.decimal("Phase spacing (mm)", "phaseSpacingMm")}
      {kit.text("Support type", "supportType")}
      {kit.text("Manufacturer reference", "supportManufacturerReference")}
      {kit.decimal("Declared maximum support spacing (mm)", "supportDeclaredMaxSpacingMm")}
      {kit.decimal("Declared peak withstand (kA)", "supportDeclaredPeakKa")}
      {kit.reference(["supportDocument", "supportRevisionOrDate", "supportPageOrClause"])}
    </OptionalBlock>
  );
}, SUPPORTS_KEYS);

const NEUTRAL_KEYS = [
  "hasNeutral",
  "neutralProposedPercentOfPhase",
  "neutralHarmonicDataAvailable",
  "neutralCalculatedCurrentA",
  "neutralRatingA",
  "neutralDocument",
  "neutralRevisionOrDate",
  "neutralPageOrClause",
] as const;

type NeutralValues = Pick<BusbarSelectionDraft, (typeof NEUTRAL_KEYS)[number]>;

export const NeutralBlock = blockMemo(function NeutralBlock({
  draft,
  onChange,
  disabled,
}: BlockProps<NeutralValues>) {
  const kit = fieldKit(draft, onChange);
  return (
    <OptionalBlock
      title="Neutral bar"
      filled={anyFilled(...NEUTRAL_KEYS.map((key) => draft[key]))}
      disabled={disabled}
    >
      {kit.yesNo("Neutral bar fitted", "hasNeutral")}
      {kit.decimal("Neutral size (% of phase)", "neutralProposedPercentOfPhase")}
      {kit.yesNo("Harmonic data available", "neutralHarmonicDataAvailable")}
      {kit.decimal("Calculated neutral current (A)", "neutralCalculatedCurrentA")}
      {kit.decimal("Neutral bar rated current (A)", "neutralRatingA")}
      {kit.reference(["neutralDocument", "neutralRevisionOrDate", "neutralPageOrClause"])}
    </OptionalBlock>
  );
}, NEUTRAL_KEYS);

const PROTECTIVE_EARTH_KEYS = [
  "earthFaultCurrentKa",
  "earthClearingTimeS",
  "earthAreaMm2",
  "earthKFactor",
  "earthKSource",
  "earthDocument",
  "earthRevisionOrDate",
  "earthPageOrClause",
] as const;

type ProtectiveEarthValues = Pick<BusbarSelectionDraft, (typeof PROTECTIVE_EARTH_KEYS)[number]>;

export const ProtectiveEarthBlock = blockMemo(function ProtectiveEarthBlock({
  draft,
  onChange,
  disabled,
}: BlockProps<ProtectiveEarthValues>) {
  const kit = fieldKit(draft, onChange);
  return (
    <OptionalBlock
      title="Protective earth bar"
      filled={anyFilled(...PROTECTIVE_EARTH_KEYS.map((key) => draft[key]))}
      disabled={disabled}
    >
      {kit.decimal("Earth fault current (kA)", "earthFaultCurrentKa")}
      {kit.decimal("Clearing time (s)", "earthClearingTimeS")}
      {kit.decimal("PE bar area (mm²)", "earthAreaMm2")}
      {kit.decimal("k factor", "earthKFactor")}
      {kit.text("k factor source", "earthKSource")}
      {kit.reference(["earthDocument", "earthRevisionOrDate", "earthPageOrClause"])}
    </OptionalBlock>
  );
}, PROTECTIVE_EARTH_KEYS);

const CLEARANCE_KEYS = [
  "clearancePhasePhase",
  "clearancePhaseEarth",
  "clearanceCreepage",
  "clearanceSupportShortCircuitStrength",
  "clearanceDocument",
  "clearanceRevisionOrDate",
  "clearancePageOrClause",
] as const;

type ClearanceValues = Pick<BusbarSelectionDraft, (typeof CLEARANCE_KEYS)[number]>;

export const ClearanceBlock = blockMemo(function ClearanceBlock({
  draft,
  onChange,
  disabled,
}: BlockProps<ClearanceValues>) {
  const kit = fieldKit(draft, onChange);
  const outcomes = verificationOutcomeSchema.options;
  return (
    <OptionalBlock
      title="Clearances"
      filled={anyFilled(...CLEARANCE_KEYS.map((key) => draft[key]))}
      disabled={disabled}
    >
      {kit.choice("Phase-to-phase clearance", "clearancePhasePhase", outcomes, VERIFICATION_OUTCOME_LABELS)}
      {kit.choice("Phase-to-earth clearance", "clearancePhaseEarth", outcomes, VERIFICATION_OUTCOME_LABELS)}
      {kit.choice("Creepage", "clearanceCreepage", outcomes, VERIFICATION_OUTCOME_LABELS)}
      {kit.choice(
        "Support short-circuit strength",
        "clearanceSupportShortCircuitStrength",
        outcomes,
        VERIFICATION_OUTCOME_LABELS,
      )}
      {kit.reference([
        "clearanceDocument",
        "clearanceRevisionOrDate",
        "clearancePageOrClause",
      ])}
    </OptionalBlock>
  );
}, CLEARANCE_KEYS);
